'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface ForgotFormData {
  email: string;
}

interface Props {
  onBack: () => void;
}

export default function ForgotPasswordForm({ onBack }: Props) {
  const { resetPassword } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<ForgotFormData>();

  const onSubmit = async (data: ForgotFormData) => {
    setLoading(true);
    try {
      await resetPassword(data.email);
      setSubmitted(true);
      toast.success('Reset link sent! Check your inbox 📬');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8 text-sm"
      >
        <ArrowLeft size={16} />
        Back to sign in
      </button>

      <h1 className="text-3xl font-bold text-foreground mb-2">Forgot password?</h1>
      <p className="text-muted-foreground mb-8">
        No worries. We'll send you a reset link.
      </p>

      {submitted ? (
        <div className="glass rounded-2xl p-8 border border-green-500/20 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-green-400" />
          </div>
          <p className="text-foreground font-semibold mb-2">Check your inbox</p>
          <p className="text-muted-foreground text-sm">
            We've sent a reset link. It expires in 1 hour.
          </p>
          <button
            onClick={onBack}
            className="mt-6 text-sm text-primary hover:underline"
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Email address
            </label>
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+$/, message: 'Enter a valid email' },
                })}
                placeholder="arjun@example.com"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-input border border-[var(--glass-border)] text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 transition-colors"
              />
            </div>
            {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 rounded-2xl gradient-bg text-white font-semibold text-sm disabled:opacity-60 hover:opacity-90 active:scale-[0.98] transition-all duration-150"
            style={{ minHeight: '52px' }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Sending…
              </span>
            ) : (
              'Send reset link'
            )}
          </button>
        </form>
      )}
    </div>
  );
}